import { useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSendAppUsageStats } from "../graphql/useSendAppUsageStats";
import { useSessionTrackingUsagestats } from "../contexts/sessiontrackingusagestats";
import { APP_USAGE_EVENT_TYPE, APP_USAGE_EVENT } from "../utils/stats";
import { setIdentityOrigin } from "../reducers/reducerIdentity";

export const useUsageStats = () => {
    const { sendAppUsageStats } = useSendAppUsageStats();
    const dispatch = useDispatch();
    const identityOrigin = useSelector((state) => state?.identity?.origin);
    const {
        projectRef,
        pendingSignInTracking,
        pendingSignUpTracking,
        markSignInTrackingAsPending,
        markSignInTrackingAsSent,
        markSignUpTrackingAsPending,
        markSignUpTrackingAsSent,
    } = useSessionTrackingUsagestats();

    const isFlushingSignInRef = useRef(false);
    const isFlushingSignUpRef = useRef(false);

    const sendSignInEvent = useCallback(
        (authProvider) =>
            sendAppUsageStats({
                type: APP_USAGE_EVENT_TYPE.USER_ACTION,
                event: APP_USAGE_EVENT.SIGN_IN,
                data: { authProvider },
            }).catch((error) => {
                console.error("Error tracking SIGN_IN:", error);
            }),
        [sendAppUsageStats]
    );

    const sendSignUpEvent = useCallback(
        (signUpOrigin) =>
            sendAppUsageStats({
                type: APP_USAGE_EVENT_TYPE.USER_ACTION,
                event: APP_USAGE_EVENT.SIGN_UP,
                data: { signUpOrigin },
            }).catch((error) => {
                console.error("Error tracking SIGN_UP:", error);
            }),
        [sendAppUsageStats]
    );

    const trackSignIn = useCallback(
        (authProvider) => {
            if (!projectRef) {
                markSignInTrackingAsPending({ authProvider });
                return Promise.resolve();
            }
            return sendSignInEvent(authProvider);
        },
        [projectRef, markSignInTrackingAsPending, sendSignInEvent]
    );

    const trackSignUp = useCallback(
        (signUpOrigin) => {
            const origin = identityOrigin || signUpOrigin || "PWA_PORTAL";

            console.log("trackSignUp origin:", origin);
            if (!origin) {
                return Promise.resolve();
            }

            const shouldClearOrigin = !!identityOrigin;

            if (!projectRef) {
                markSignUpTrackingAsPending({ signUpOrigin: origin });
                return Promise.resolve();
            }

            return sendSignUpEvent(origin).finally(() => {
                if (shouldClearOrigin) {
                    dispatch(setIdentityOrigin(null));
                }
            });
        },
        [projectRef, markSignUpTrackingAsPending, sendSignUpEvent, identityOrigin, dispatch]
    );

    const flushPendingUsageEvents = useCallback(() => {
        if (!projectRef) {
            return Promise.resolve();
        }

        const flushes = [];

        if (pendingSignInTracking && !isFlushingSignInRef.current) {
            isFlushingSignInRef.current = true;
            flushes.push(
                sendSignInEvent(pendingSignInTracking.authProvider).finally(() => {
                    isFlushingSignInRef.current = false;
                    markSignInTrackingAsSent();
                })
            );
        }

        if (pendingSignUpTracking && !isFlushingSignUpRef.current) {
            isFlushingSignUpRef.current = true;
            const shouldClearOrigin = !!identityOrigin && identityOrigin === pendingSignUpTracking.signUpOrigin;
            flushes.push(
                sendSignUpEvent(pendingSignUpTracking.signUpOrigin).finally(() => {
                    isFlushingSignUpRef.current = false;
                    markSignUpTrackingAsSent();
                    if (shouldClearOrigin) {
                        dispatch(setIdentityOrigin(null));
                    }
                })
            );
        }

        if (!flushes.length) {
            return Promise.resolve();
        }

        return Promise.all(flushes);
    }, [
        projectRef,
        pendingSignInTracking,
        pendingSignUpTracking,
        sendSignInEvent,
        sendSignUpEvent,
        markSignInTrackingAsSent,
        markSignUpTrackingAsSent,
        identityOrigin,
        dispatch,
    ]);

    const trackAnonymousAccess = () => {
        return sendAppUsageStats({
            type: APP_USAGE_EVENT_TYPE.USER_ACTION,
            event: APP_USAGE_EVENT.ANONYMOUS_ACCESS,
        }).catch((error) => {
            console.error("Error tracking ANONYMOUS_ACCESS:", error);
        });
    };

    const trackAddRoom = (roomId) => {
        return sendAppUsageStats({
            type: APP_USAGE_EVENT_TYPE.USER_ACTION,
            event: APP_USAGE_EVENT.ADD_ROOM,
            data: { roomId },
        }).catch((error) => {
            console.error("Error tracking ADD_ROOM:", error);
        });
    };

    const trackScreenView = (screenViewName) => {
        return sendAppUsageStats({
            type: APP_USAGE_EVENT_TYPE.USER_ACTION,
            event: APP_USAGE_EVENT.SCREEN_VIEW,
            data: { screenViewName },
        }).catch((error) => {
            console.error("Error tracking SCREEN_VIEW:", error);
        });
    };

    const trackAppOpen = () => {
        return sendAppUsageStats({
            type: APP_USAGE_EVENT_TYPE.SESSION_EVENT,
            event: APP_USAGE_EVENT.APP_OPEN,
        }).catch((error) => {
            console.error("Error tracking APP_OPEN:", error);
        });
    };

    return {
        trackSignIn,
        trackSignUp,
        trackAnonymousAccess,
        trackAddRoom,
        trackScreenView,
        trackAppOpen,
        flushPendingUsageEvents,
    };
};
